import { useEffect, useState } from 'react';
import { Stack, Text, Banner, Skeleton, cx } from '../../ui';
import {
  NOTIF_EVENTS,
  fetchSettings,
  setChannelEnabled,
  type NotificationSetting,
  type NotifyChannel,
} from '../../../lib/notifications-admin';

/** A labelled on/off switch (role=switch, keyboard-operable). */
function Switch({
  on, busy, onToggle, label,
}: { on: boolean; busy: boolean; onToggle: () => void; label: string }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      disabled={busy}
      onClick={onToggle}
      className={cx(
        'relative inline-flex h-[22px] w-[40px] shrink-0 items-center rounded-full border transition-colors duration-150',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:opacity-50',
        on ? 'border-ink bg-ink' : 'border-border bg-bone',
      )}
    >
      <span
        aria-hidden="true"
        className={cx(
          'inline-block h-[16px] w-[16px] rounded-full bg-surface transition-transform duration-150',
          on ? 'translate-x-[21px]' : 'translate-x-[3px]',
        )}
      />
    </button>
  );
}

export function ChannelsTab() {
  const [settings, setSettings] = useState<Map<string, NotificationSetting> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [savingKey, setSavingKey] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    fetchSettings()
      .then((rows) => { if (alive) setSettings(new Map(rows.map((r) => [r.event_type, r]))); })
      .catch((e) => { if (alive) setError(e?.message ?? 'Failed to load settings.'); });
    return () => { alive = false; };
  }, []);

  async function toggle(event_type: string, channel: NotifyChannel, current: boolean) {
    const key = `${event_type}:${channel}`;
    setSavingKey(key);
    setError(null);
    // Optimistic flip.
    setSettings((prev) => {
      if (!prev) return prev;
      const next = new Map(prev);
      const row = { ...(next.get(event_type) as NotificationSetting) };
      if (channel === 'email') row.email_enabled = !current; else row.sms_enabled = !current;
      next.set(event_type, row);
      return next;
    });
    try {
      await setChannelEnabled(event_type, channel, !current);
    } catch (e: any) {
      // Roll back.
      setSettings((prev) => {
        if (!prev) return prev;
        const next = new Map(prev);
        const row = { ...(next.get(event_type) as NotificationSetting) };
        if (channel === 'email') row.email_enabled = current; else row.sms_enabled = current;
        next.set(event_type, row);
        return next;
      });
      setError(e?.message ?? 'Could not save - try again.');
    } finally {
      setSavingKey(null);
    }
  }

  if (error && !settings) return <Banner tone="danger" title="Couldn't load">{error}</Banner>;

  return (
    <Stack gap="group">
      {error && <Banner tone="danger" title="Couldn't save" onClose={() => setError(null)}>{error}</Banner>}

      <div className="overflow-hidden rounded-sm border border-border">
        <div className="grid grid-cols-[1fr_auto_auto] items-center gap-x-6 gap-y-0 bg-bone px-4 py-2 font-body text-label font-semibold uppercase tracking-[0.04em] text-text-muted">
          <span>Event</span>
          <span className="w-[44px] text-center">Email</span>
          <span className="w-[44px] text-center">SMS</span>
        </div>

        {!settings
          ? [0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="grid grid-cols-[1fr_auto_auto] items-center gap-x-6 border-t border-border px-4 py-3">
                <Skeleton className="h-4 w-40" />
                <Skeleton className="h-[22px] w-[40px] rounded-full" />
                <Skeleton className="h-[22px] w-[40px] rounded-full" />
              </div>
            ))
          : NOTIF_EVENTS.map((ev) => {
              const s = settings.get(ev.event) ?? { event_type: ev.event, email_enabled: true, sms_enabled: true };
              const hasEmail = ev.channels.includes('email');
              const hasSms = ev.channels.includes('sms');
              return (
                <div key={ev.event} className="grid grid-cols-[1fr_auto_auto] items-center gap-x-6 border-t border-border px-4 py-3">
                  <div className="min-w-0">
                    <Text role="body" as="p" className="font-semibold">{ev.label}</Text>
                    <Text role="caption" muted as="p">{ev.description}</Text>
                  </div>
                  <div className="flex w-[44px] justify-center">
                    {hasEmail ? (
                      <Switch
                        on={s.email_enabled}
                        busy={savingKey === `${ev.event}:email`}
                        onToggle={() => toggle(ev.event, 'email', s.email_enabled)}
                        label={`${ev.label} email`}
                      />
                    ) : <span className="text-text-faint">-</span>}
                  </div>
                  <div className="flex w-[44px] justify-center">
                    {hasSms ? (
                      <Switch
                        on={s.sms_enabled}
                        busy={savingKey === `${ev.event}:sms`}
                        onToggle={() => toggle(ev.event, 'sms', s.sms_enabled)}
                        label={`${ev.label} SMS`}
                      />
                    ) : <span className="text-text-faint">-</span>}
                  </div>
                </div>
              );
            })}
      </div>

      <Text role="caption" muted as="p">
        Turning a channel off here stops that event sending on that channel - the dispatcher
        checks these before every send. Changes apply within a minute.
      </Text>
    </Stack>
  );
}
